import React from 'react';
import { Download, FileText, FileSpreadsheet, Clock, CheckCircle, XCircle, Loader, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

export interface ReportHistoryItem {
  id: string;
  reportType: 'timecards-analysis' | 'timecards-weekly-totals';
  reportName: string;
  parameters: any;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: Date;
  completedAt?: Date;
  csvUrl?: string;
  excelUrl?: string;
  error?: string;
}

interface ReportHistoryProps {
  reports: ReportHistoryItem[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onDeleteReport?: (reportId: string) => void;
}

const ReportHistory: React.FC<ReportHistoryProps> = ({ reports, isExpanded, onToggleExpanded, onDeleteReport }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'processing':
        return <Loader className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-neutral-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[status] || 'bg-neutral-100 text-neutral-800 border-neutral-200'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReportTypeName = (type: string) => {
    switch (type) {
      case 'timecards-analysis':
        return 'Timecards Analysis';
      case 'timecards-weekly-totals':
        return 'Weekly Totals';
      default:
        return type;
    }
  };

  const formatParameters = (report: ReportHistoryItem) => {
    if (report.reportType === 'timecards-analysis') {
      return `Week: ${new Date(report.parameters.weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (report.reportType === 'timecards-weekly-totals') {
      return `${new Date(report.parameters.fromWeekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(report.parameters.toWeekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return '';
  };

  return (
    <div className="border-t border-neutral-200 bg-white">
      {/* Header */}
      <button
        onClick={onToggleExpanded}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-neutral-600" />
          <div className="text-left">
            <h3 className="text-sm font-semibold text-neutral-900">Report History</h3>
            <p className="text-xs text-neutral-600">
              {reports.length} report{reports.length !== 1 ? 's' : ''} generated
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {reports.filter(r => r.status === 'processing').length > 0 && (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <Loader className="h-3 w-3 animate-spin" />
              {reports.filter(r => r.status === 'processing').length} processing
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-neutral-400" />
          ) : (
            <ChevronUp className="h-5 w-5 text-neutral-400" />
          )}
        </div>
      </button>

      {/* Report List with Vertical Scrollbar */}
      {isExpanded && (
        <div className="px-6 pb-4">
          {reports.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
              <p className="text-sm">No reports generated yet</p>
              <p className="text-xs mt-1">Generated reports will appear here</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-scroll space-y-1 pr-2" style={{ overflowY: 'scroll' }}>
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-neutral-50 hover:bg-neutral-100 rounded border border-neutral-200 px-4 py-2 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    {/* Report Info - Single Line */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getStatusIcon(report.status)}
                      
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-neutral-900 truncate">
                          {getReportTypeName(report.reportType)}
                        </span>
                        {getStatusBadge(report.status)}
                      </div>
                      
                      <span className="text-xs text-neutral-600 truncate">
                        {formatParameters(report)}
                      </span>
                      
                      <span className="text-xs text-neutral-400 hidden md:block">
                        {formatDate(report.createdAt)}
                      </span>
                    </div>

                    {/* Actions - Download Links and Delete */}
                    <div className="flex gap-1 flex-shrink-0">
                      {report.status === 'completed' && (
                        <>
                          {report.csvUrl && (
                            <a
                              href={report.csvUrl}
                              download={`${report.reportName.replace(/\s+/g, '_')}_${new Date(report.createdAt).toISOString().slice(0, 10)}.csv`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                              title="Download CSV"
                            >
                              <Download className="h-3 w-3" />
                              CSV
                            </a>
                          )}
                          {report.excelUrl && (
                            <a
                              href={report.excelUrl}
                              download={`${report.reportName.replace(/\s+/g, '_')}_${new Date(report.createdAt).toISOString().slice(0, 10)}.xlsx`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
                              title="Download Excel"
                            >
                              <Download className="h-3 w-3" />
                              Excel
                            </a>
                          )}
                        </>
                      )}
                      
                      {/* Delete Button */}
                      {onDeleteReport && (
                        <button
                          onClick={() => onDeleteReport(report.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                          title="Delete Report"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    
                    {report.error && (
                      <span className="text-xs text-red-600 truncate max-w-xs" title={report.error}>
                        Error: {report.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportHistory;
